import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useMutation } from '@tanstack/react-query';
import { api } from '../api/client';
import { fmtMoney, fmtMoney2, fmtInt, fmtPct, fmtDays } from '../lib/fmt';
import { useAppCtx } from '../context/AppCtx';
import type { ItemOut, WorkflowStatus } from '../types/api';
import { ABC_COLOR, STATUS_COLOR, MARGIN_COLOR, WF_COLOR, WF_LABEL } from '../lib/colors';

const WF_OPTIONS: WorkflowStatus[] = [
  'pendente', 'cot_recebida', 'em_validacao', 'aprov_comercial',
  'reprov_comercial', 'aguard_financeiro', 'aprov_financeiro',
  'pedido', 'agendado', 'receb_parcial', 'receb_total',
];

export function Badge({ txt, map }: { txt: string; map: Record<string, string> }) {
  return <span className={`badge ${map[txt] ?? 'bg-slate-500/20 text-slate-300'}`}>{txt}</span>;
}

function DetailRow({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5 border-b border-mbc-line/60 last:border-0">
      <span className="text-xs text-mbc-mute shrink-0">{label}</span>
      <span className={`text-sm text-right ${accent ?? 'text-mbc-soft'}`}>{value}</span>
    </div>
  );
}

function ProgressBar({ pct, colorClass }: { pct: number; colorClass: string }) {
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-mbc-line">
      <div
        className={`h-full rounded-full transition-all duration-500 ${colorClass}`}
        style={{ width: `${Math.min(pct * 100, 100)}%` }}
      />
    </div>
  );
}

function splitPlatforms(plataformas: string | null | undefined) {
  if (!plataformas) return [];
  const raw = plataformas.toLowerCase();
  const aliases: Array<[string, RegExp]> = [
    ['amazon', /amazon/],
    ['mercado livre', /mercado livre|mercadolivre|meli/],
    ['magalu', /magalu|magazine luiza/],
    ['shopee', /shopee/],
    ['shopify', /shopify/],
    ['americanas', /americanas/],
    ['carrefour', /carrefour/],
  ];
  const matched = aliases.filter(([, p]) => p.test(raw)).map(([label]) => label);
  if (matched.length > 0) return matched;
  return plataformas.split(/[|,;/]+/).map(p => p.trim().toLowerCase()).filter(Boolean);
}

interface SkuDrawerProps {
  item: ItemOut | null;
  onClose: () => void;
  onSaved?: () => void;
}

export function SkuDrawer({ item, onClose, onSaved }: SkuDrawerProps) {
  const { horizon } = useAppCtx();
  const [activeTab, setActiveTab] = useState<'geral' | 'workflow'>('geral');
  const [entered, setEntered] = useState(false);

  const [wfStatus, setWfStatus] = useState<WorkflowStatus>('pendente');
  const [wfQtd, setWfQtd] = useState('');
  const [wfPrecoAlvo, setWfPrecoAlvo] = useState('');
  const [wfFornecedor, setWfFornecedor] = useState('');
  const [wfObservacoes, setWfObservacoes] = useState('');
  const [baseline, setBaseline] = useState({
    status: 'pendente' as WorkflowStatus,
    qtd: '',
    precoAlvo: '',
    fornecedor: '',
    observacoes: '',
  });
  const [savedOk, setSavedOk] = useState(false);

  useEffect(() => {
    if (!item) {
      setEntered(false);
      return;
    }

    const raf = requestAnimationFrame(() => setEntered(true));

    const nextStatus    = item.workflow_status as WorkflowStatus;
    const nextQtd       = String(item.workflow_qtd ?? item.sugestao_compra);
    const nextPrecoAlvo = Number(item.workflow_preco_alvo ?? 0).toFixed(2);
    const nextFornecedor  = item.workflow_fornecedor ?? item.fornecedor ?? '';
    const nextObservacoes = item.workflow_observacoes ?? '';

    setWfStatus(nextStatus);
    setWfQtd(nextQtd);
    setWfPrecoAlvo(nextPrecoAlvo);
    setWfFornecedor(nextFornecedor);
    setWfObservacoes(nextObservacoes);
    setBaseline({ status: nextStatus, qtd: nextQtd, precoAlvo: nextPrecoAlvo, fornecedor: nextFornecedor, observacoes: nextObservacoes });
    setSavedOk(false);
    setActiveTab('geral');

    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(raf);
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [item, onClose]);

  const statusMut = useMutation({
    mutationFn: (payload: {
      status: WorkflowStatus;
      qtd: number;
      preco_alvo: number;
      fornecedor: string | null;
      observacoes: string;
    }) => api.patch(`/analysis/items/${item!.sku}/workflow`, payload),
    onSuccess: () => {
      setBaseline({ status: wfStatus, qtd: wfQtd, precoAlvo: wfPrecoAlvo, fornecedor: wfFornecedor, observacoes: wfObservacoes });
      setSavedOk(true);
      onSaved?.();
      setTimeout(() => setSavedOk(false), 2500);
    },
  });

  if (!item) return null;

  const precoVenda  = Number(item.preco_venda);
  const custo       = Number(item.custo_medio);
  const margem      = Number(item.margem_pct);
  const precoAlvo15 = precoVenda * 0.85;
  const reducao     = Math.max(0, custo - item.preco_teto);
  const precisaNeg  = item.classe_margem === 'Negociar' || item.classe_margem === 'Bloqueado';
  const plataformas = splitPlatforms(item.plataformas);

  const parsedQtd       = Number.parseInt(wfQtd, 10);
  const parsedPrecoAlvo = Number.parseFloat(wfPrecoAlvo.replace(',', '.'));
  const isValidWorkflow = Number.isInteger(parsedQtd) && parsedQtd >= 0 && Number.isFinite(parsedPrecoAlvo) && parsedPrecoAlvo >= 0;
  const isDirty = (
    wfStatus !== baseline.status ||
    wfQtd !== baseline.qtd ||
    wfPrecoAlvo !== baseline.precoAlvo ||
    wfFornecedor !== baseline.fornecedor ||
    wfObservacoes !== baseline.observacoes
  );

  const coberturaPct = item.cobertura_dias != null ? Math.min(item.cobertura_dias / horizon, 1) : 0;
  const coberturaColor =
    item.status_estoque === 'Zerado' || item.status_estoque === 'Crítico' ? 'bg-rose-400'
    : item.status_estoque === 'Excesso' ? 'bg-sky-400'
    : 'bg-emerald-400';

  const totalNeed    = item.estoque_total + item.pedidos_aberto + item.sugestao_compra;
  const stockFillPct = totalNeed > 0 ? (item.estoque_total + item.pedidos_aberto) / totalNeed : 1;
  const stockFillColor = stockFillPct > 0.7 ? 'bg-emerald-400' : stockFillPct > 0.3 ? 'bg-amber-400' : 'bg-rose-400';

  return createPortal(
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Fechar detalhe"
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />

      {/* Side Drawer */}
      <aside
        className={`absolute right-0 top-0 flex h-full w-full max-w-[480px] flex-col border-l border-mbc-line bg-mbc-panel shadow-2xl transition-transform duration-300 ease-out ${
          entered ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* ── Header ── */}
        <div className="shrink-0 border-b border-mbc-line bg-mbc-panel p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="font-mono text-sm text-mbc-bluel">SKU {item.sku}</div>
              <h3 className="mt-1 truncate font-bold leading-tight text-white" title={item.nome}>
                {item.nome}
              </h3>
              <div className="mt-2 flex flex-wrap gap-2">
                <Badge txt={item.classe} map={ABC_COLOR} />
                <Badge txt={item.status_estoque} map={STATUS_COLOR} />
                {item.sugestao_compra > 0 && <Badge txt={item.classe_margem} map={MARGIN_COLOR} />}
                <Badge txt={WF_LABEL[wfStatus] ?? wfStatus} map={WF_COLOR} />
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 text-3xl leading-none text-mbc-mute transition-colors hover:text-white"
            >
              ×
            </button>
          </div>

          {/* Tabs */}
          <div className="mt-4 flex gap-1 rounded-lg bg-mbc-bg p-1">
            {(['geral', 'workflow'] as const).map(tab => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={`flex-1 rounded-md py-1.5 text-xs font-semibold transition-colors ${
                  activeTab === tab
                    ? 'bg-mbc-panel text-white shadow-sm'
                    : 'text-mbc-mute hover:text-mbc-soft'
                }`}
              >
                {tab === 'geral' ? 'Visão Geral' : 'Workflow'}
              </button>
            ))}
          </div>
        </div>

        {/* ── Content ── */}
        <div className="flex-1 overflow-y-auto p-5">
          {activeTab === 'geral' ? (
            <div className="space-y-4">

              {/* Mini KPIs */}
              <div className="grid grid-cols-2 gap-3">
                <div className="card kpi p-4">
                  <div className="text-xs uppercase tracking-wider text-mbc-mute">Vendas 90d</div>
                  <div className="mt-2 text-2xl font-bold text-white">{fmtInt(item.vendas_90d)}</div>
                  <div className="mt-1 text-xs text-mbc-bluel">
                    ≈ {fmtInt(Number(item.consumo_diario) * 45)} un/45d
                  </div>
                </div>
                <div className="card kpi p-4">
                  <div className="text-xs uppercase tracking-wider text-mbc-mute">Consumo diário</div>
                  <div className="mt-2 text-2xl font-bold text-white">
                    {Number(item.consumo_diario).toFixed(1)}
                  </div>
                  <div className="mt-1 text-xs text-mbc-mute">un/dia</div>
                </div>
                <div className="card kpi col-span-2 p-4">
                  <div className="flex items-center justify-between">
                    <div className="text-xs uppercase tracking-wider text-mbc-mute">Cobertura atual</div>
                    <span className="text-sm font-bold text-white">{fmtDays(item.cobertura_dias)}</span>
                  </div>
                  <div className="mt-2">
                    <ProgressBar pct={coberturaPct} colorClass={coberturaColor} />
                  </div>
                  <div className="mt-1.5 flex justify-between text-xs text-mbc-mute">
                    <span>{item.status_estoque}</span>
                    <span>Meta: {horizon}d</span>
                  </div>
                </div>
              </div>

              {/* Projeção & Ruptura */}
              <div className="card p-4">
                <div className="mb-3 text-xs uppercase tracking-wider text-mbc-mute">
                  Projeção &amp; Ruptura
                </div>
                {item.sugestao_compra > 0 && (
                  <div className="mb-3">
                    <div className="mb-1 flex justify-between text-xs text-mbc-mute">
                      <span>Estoque atual vs necessário</span>
                      <span>{Math.round(stockFillPct * 100)}%</span>
                    </div>
                    <ProgressBar pct={stockFillPct} colorClass={stockFillColor} />
                  </div>
                )}
                <DetailRow label="Estoque total (CD)" value={fmtInt(item.estoque_total)} />
                {item.pedidos_aberto > 0 && (
                  <DetailRow label="Em trânsito (POs)" value={fmtInt(item.pedidos_aberto)} accent="text-sky-300" />
                )}
                <DetailRow
                  label="Disponibilidade total"
                  value={fmtInt(item.estoque_total + item.pedidos_aberto)}
                  accent="font-semibold text-white"
                />
                <DetailRow
                  label="Sugestão de compra"
                  value={fmtInt(item.sugestao_compra)}
                  accent="font-bold text-mbc-orangel"
                />
                <DetailRow
                  label="Ruptura projetada"
                  value={fmtInt(item.ruptura_qtd)}
                  accent={item.ruptura_qtd > 0 ? 'font-semibold text-rose-300' : 'text-emerald-300'}
                />
                <DetailRow
                  label="Faturamento em risco"
                  value={fmtMoney(Number(item.valor_perdido))}
                  accent={Number(item.valor_perdido) > 0 ? 'text-rose-300' : 'text-emerald-300'}
                />
              </div>

              {/* Margem & Preços */}
              <div className="card p-4">
                <div className="mb-3 text-xs uppercase tracking-wider text-mbc-mute">
                  Margem &amp; Preços
                </div>
                <DetailRow label="Preço médio de venda"    value={fmtMoney2(precoVenda)} />
                <DetailRow label="Custo atual"             value={fmtMoney2(custo)} />
                <DetailRow
                  label="Margem atual"
                  value={fmtPct(margem)}
                  accent={margem < 0.1 ? 'text-rose-300' : 'text-emerald-300'}
                />
                <DetailRow label="Preço-teto (margem 10%)" value={fmtMoney2(item.preco_teto)} accent="text-white" />
                <DetailRow label="Preço-alvo (margem 15%)" value={fmtMoney2(precoAlvo15)} accent="text-mbc-orangel" />
                {precisaNeg && (
                  <DetailRow
                    label="Redução necessária"
                    value={`${fmtMoney2(reducao)} (${fmtPct(item.pct_negociacao)})`}
                    accent="text-amber-300"
                  />
                )}
                <DetailRow label="Investimento total" value={fmtMoney(item.investimento)} />
              </div>

              {/* Referência */}
              <div className="card p-4">
                <div className="mb-3 text-xs uppercase tracking-wider text-mbc-mute">Referência</div>
                <DetailRow label="Marca"      value={item.marca      ?? '—'} />
                <DetailRow label="Fornecedor" value={item.fornecedor ?? '—'} />
                <DetailRow label="Curva ABC"  value={item.classe} />
                {plataformas.length > 0 && (
                  <div className="flex items-start justify-between gap-3 pt-2">
                    <span className="shrink-0 text-xs text-mbc-mute">Plataformas</span>
                    <div className="flex flex-wrap justify-end gap-1">
                      {plataformas.map(p => (
                        <span key={p} className="badge bg-mbc-blue/20 text-mbc-bluel">{p}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Regras Comerciais */}
              {(item.regra_lead_time_dias != null || item.regra_pedido_minimo != null || item.regra_prazo_pagamento) && (
                <div className="card p-4">
                  <div className="mb-3 text-xs uppercase tracking-wider text-mbc-mute">Regras Comerciais</div>
                  {item.regra_lead_time_dias != null && (
                    <DetailRow label="Lead time cadastrado" value={`${item.regra_lead_time_dias} dias`} accent="text-sky-300" />
                  )}
                  {item.regra_prazo_pagamento && (
                    <DetailRow label="Prazo de pagamento" value={item.regra_prazo_pagamento} />
                  )}
                  {item.regra_pedido_minimo != null && (
                    <DetailRow
                      label="Pedido mínimo"
                      value={fmtMoney(item.regra_pedido_minimo)}
                      accent={item.investimento < item.regra_pedido_minimo ? 'text-amber-300' : 'text-emerald-300'}
                    />
                  )}
                  {item.regra_pedido_minimo != null && item.investimento < item.regra_pedido_minimo && (
                    <div className="mt-2 rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
                      Investimento sugerido ({fmtMoney(item.investimento)}) está abaixo do pedido mínimo — considere consolidar SKUs do mesmo fornecedor.
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            /* ── Workflow Tab ── */
            <div className="space-y-4">
              <div className="card p-4">
                <div className="mb-2 text-xs uppercase tracking-wider text-mbc-mute">Status atual</div>
                <Badge txt={WF_LABEL[wfStatus] ?? wfStatus} map={WF_COLOR} />
              </div>

              <div className="card space-y-3 p-4">
                <div className="text-xs uppercase tracking-wider text-mbc-mute">Atualizar workflow</div>

                <div>
                  <label className="mb-1 block text-xs text-mbc-mute">Status</label>
                  <select
                    value={wfStatus}
                    onChange={e => setWfStatus(e.target.value as WorkflowStatus)}
                    className="w-full rounded-lg border border-mbc-line bg-mbc-panel2 px-3 py-2 text-sm text-mbc-soft focus:border-mbc-orange focus:outline-none"
                  >
                    {WF_OPTIONS.map(s => (
                      <option key={s} value={s}>{WF_LABEL[s]}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="mb-1 text-xs text-mbc-mute">Qtd a comprar</div>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={wfQtd}
                      onChange={e => setWfQtd(e.target.value)}
                      className="w-full rounded-lg border border-mbc-line bg-mbc-panel2 px-3 py-2 text-sm text-mbc-soft focus:border-mbc-orange focus:outline-none"
                    />
                  </div>
                  <div>
                    <div className="mb-1 text-xs text-mbc-mute">Preço-alvo (R$)</div>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={wfPrecoAlvo}
                      onChange={e => setWfPrecoAlvo(e.target.value)}
                      className="w-full rounded-lg border border-mbc-line bg-mbc-panel2 px-3 py-2 text-sm text-mbc-orangel focus:border-mbc-orange focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <div className="mb-1 text-xs text-mbc-mute">Fornecedor</div>
                  <input
                    type="text"
                    value={wfFornecedor}
                    onChange={e => setWfFornecedor(e.target.value)}
                    className="w-full rounded-lg border border-mbc-line bg-mbc-panel2 px-3 py-2 text-sm text-mbc-soft focus:border-mbc-orange focus:outline-none"
                  />
                </div>

                <div>
                  <div className="mb-1 text-xs text-mbc-mute">Observações</div>
                  <textarea
                    rows={4}
                    value={wfObservacoes}
                    onChange={e => setWfObservacoes(e.target.value)}
                    className="w-full resize-y rounded-lg border border-mbc-line bg-mbc-panel2 px-3 py-2 text-sm text-mbc-soft focus:border-mbc-orange focus:outline-none"
                  />
                </div>

                <button
                  type="button"
                  onClick={() => statusMut.mutate({
                    status: wfStatus,
                    qtd: parsedQtd,
                    preco_alvo: parsedPrecoAlvo,
                    fornecedor: wfFornecedor.trim() || null,
                    observacoes: wfObservacoes,
                  })}
                  disabled={statusMut.isPending || !isDirty || !isValidWorkflow}
                  className="w-full rounded-xl bg-mbc-orange py-2.5 font-bold text-white transition-colors hover:bg-mbc-orangel disabled:opacity-40"
                >
                  {statusMut.isPending ? 'Salvando…' : savedOk ? '✓ Status salvo!' : 'Salvar no workflow'}
                </button>

                {statusMut.isError && (
                  <p className="text-xs text-rose-300">Erro ao salvar. Tente novamente.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </aside>
    </div>,
    document.body
  );
}
